// src/pages/platform-admin/AssignedStudent.js

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    useTheme,
    InputAdornment,
    Tooltip,
    Alert,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useMediaQuery,
    Avatar,
    Stack,
    Card,
    CardContent,
    LinearProgress
} from '@mui/material';
import {
    Search,
    Close as CloseIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Visibility as ViewIcon,
    School,
    CheckCircle,
    Cancel,
    CalendarToday,
    Update,
    Email,
    Phone,
    Flag,
    Person,
    Badge,
    VerifiedUser,
    Schedule,
    People,
    Wc,
    Description,
    SupervisorAccount,
    Assignment,
    TrendingUp,
    Pending,
    Work
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIAssignStudentList,
    APIStudentProfileImage,
    APISupervisorProfileImage
} from '../../apiServices/APIs';
import { 
    formatCreatedAtDate, 
    getImageUrl
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import ErrorState from '../../components/ErrorState';
import LoadingState from '../../components/LoadingState';
import PlatformAdminBar from '../../components/PlatformAdminBar';
import { FullWidthContainer } from '../../components/FullWidthContainer';

// Transition component for dialogs
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Helper function to get initials from user name
const getInitials = (name) => {
    if (!name) return 'U';
    return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
};

// Helper function to get avatar color based on user name
const stringToColor = (string) => {
    if (!string) return '#1976d2';
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
};

// Status options for filtering
const statusOptions = [
    { value: 'active', label: 'Active', color: 'success' },
    { value: 'completed', label: 'Completed', color: 'info' },
    { value: 'pending', label: 'Pending', color: 'warning' },
    { value: 'cancelled', label: 'Cancelled', color: 'error' }
];

const AssignedStudent = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const showToast = useCustomToast();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [assignments, setAssignments] = useState([]);
    const [stats, setStats] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [filters, setFilters] = useState({
        status: ''
    });
    
    // State for dialogs
    const [filterOptions, setFilterOptions] = useState(false);
    
    // Use ref to track if component is mounted
    const isMounted = useRef(true);

    
    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchData();
        
        return () => {
            isMounted.current = false;
        };
    }, []);
    
    // Fetch assigned students
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            const result = await apiService.list(APIAssignStudentList);
            
            if (result.success && isMounted.current) {
                const assignmentsData = Array.isArray(result.data) ? result.data.reverse() : [];
                setAssignments(assignmentsData);
                calculateStats(assignmentsData);
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError("Failed to load data");
            console.error("Fetch error:", error);
        } finally {
            if (isMounted.current) setLoading(false);
        }
    }, []);
    
    // Calculate statistics
    const calculateStats = (data) => {
        const total = data.length;
        const active = data.filter(a => a.status === 'active').length;
        const completed = data.filter(a => a.status === 'completed').length;
        const pending = data.filter(a => a.status === 'pending').length;
        const cancelled = data.filter(a => a.status === 'cancelled').length;
        
        setStats({
            total,
            active,
            completed,
            pending,
            cancelled
        });
    };
    
    // Filter based on search term and filters
    const filteredData = useMemo(() => {
        return assignments.filter(assignment => {
            const matchesSearch = searchTerm === '' ||
                assignment.student?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                assignment.supervisor?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                assignment.assignment_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                assignment.student?.email?.toLowerCase().includes(searchTerm.toLowerCase());
            
            const matchesStatus = !filters.status || assignment.status === filters.status;
            
            return matchesSearch && matchesStatus;
        });
    }, [assignments, searchTerm, filters.status]);
    
    // Pagination handlers
    const handleChangePage = useCallback((event, newPage) => setPage(newPage), []);
    const handleChangeRowsPerPage = useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);
    
    // Dialog handlers
    const handlefilterOptions = useCallback(() => setFilterOptions(true), []);
    const handleCloseOptions = useCallback(() => setFilterOptions(false), []);
    
    const handleOpenView = useCallback((assignment) => {
        setSelectedAssignment(assignment);
        setOpenViewDialog(true);
    }, []);
    
    const handleCloseView = useCallback(() => {
        setOpenViewDialog(false);
        setSelectedAssignment(null);
    }, []);
    
    // Filter handlers
    const handleFilterChange = useCallback((e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    }, []);
    
    const clearFilters = useCallback(() => {
        setFilters({
            status: ''
        });
        setSearchTerm('');
        handleCloseOptions();
    }, [handleCloseOptions]);

    // Get profile image URL
    const getStudentProfileImage = (student) => {
        if (student?.profile_image) {
            return getImageUrl(APIStudentProfileImage, student.profile_image);
        }
        return null;
    };

    const getSupervisorProfileImage = (supervisor) => {
        if (supervisor?.profile_image) {
            return getImageUrl(APISupervisorProfileImage, supervisor.profile_image);
        }
        return null;
    };

    const getStatusIcon = (status) => {
        switch(status) {
            case 'active': return <CheckCircle sx={{ color: '#4caf50', fontSize: 16 }} />;
            case 'completed': return <VerifiedUser sx={{ color: '#2196f3', fontSize: 16 }} />;
            case 'pending': return <Pending sx={{ color: '#ff9800', fontSize: 16 }} />;
            case 'cancelled': return <Cancel sx={{ color: '#f44336', fontSize: 16 }} />;
            default: return <Schedule sx={{ fontSize: 16 }} />;
        }
    };

    const getStatusColor = (status) => {
        switch(status) {
            case 'active': return 'success';
            case 'completed': return 'info';
            case 'pending': return 'warning';
            case 'cancelled': return 'error';
            default: return 'default';
        }
    };

    if (loading) {
        return <LoadingState message="Loading Assigned Students..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchData} title="Unable to Load Assigned Students" variant="research" fullPage retryText="Retry Loading" />;
    }

    return (
        <>
            <FullWidthContainer >
                <Box sx={{ backgroundColor: 'white', borderRadius: 3 }}>
                    <PlatformAdminBar notificationCount={65} />
                    
                    {/* Header Section */}
                    <Paper elevation={2} sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        mb: { xs: 2, sm: 3 }, 
                        borderRadius: 3,
                        backgroundColor: 'white',
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            flexWrap: 'wrap', 
                            gap: 2,
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            <Typography variant="h6" sx={{ 
                                fontWeight: 'bold', 
                                color: theme.palette.primary.main, 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}>
                                <Assignment /> Assigned Students
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<FilterIcon />}
                                    onClick={handlefilterOptions}
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: '8px',
                                        flex: { xs: 1, sm: 'none' }
                                    }}
                                    size={isMobile ? "small" : "medium"}
                                >
                                    View Options
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="info"
                                    startIcon={<RefreshIcon />}
                                    onClick={fetchData}
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: '8px',
                                        flex: { xs: 1, sm: 'none' }
                                    }}
                                    size={isMobile ? "small" : "medium"}
                                >
                                    Refresh
                                </Button>
                            </Box>
                        </Box>
                    </Paper>

                    {/* Stats Cards */}
                    {stats && (
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={6} sm={3}>
                                <Card sx={{ 
                                    textAlign: 'center', 
                                    bgcolor: theme.palette.info.light + '20',
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'translateY(-4px)' },
                                    cursor: 'pointer'
                                }}>
                                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                        <People sx={{ fontSize: { xs: 24, sm: 32 }, color: theme.palette.info.main, mb: 1 }} />
                                        <Typography variant="h4" sx={{ 
                                            fontWeight: 'bold',
                                            fontSize: { xs: '1.5rem', sm: '2rem' }
                                        }}>
                                            {stats.total || 0}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, color: 'text.secondary' }}>
                                            Total Assignments
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Card sx={{ 
                                    textAlign: 'center', 
                                    bgcolor: theme.palette.success.light + '20',
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'translateY(-4px)' },
                                    cursor: 'pointer'
                                }}>
                                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                        <CheckCircle sx={{ fontSize: { xs: 24, sm: 32 }, color: theme.palette.success.main, mb: 1 }} />
                                        <Typography variant="h4" sx={{ 
                                            fontWeight: 'bold',
                                            fontSize: { xs: '1.5rem', sm: '2rem' }
                                        }}>
                                            {stats.active || 0}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, color: 'text.secondary' }}>
                                            Active Assignments
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Card sx={{ 
                                    textAlign: 'center', 
                                    bgcolor: theme.palette.info.light + '20',
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'translateY(-4px)' },
                                    cursor: 'pointer'
                                }}>
                                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                        <VerifiedUser sx={{ fontSize: { xs: 24, sm: 32 }, color: theme.palette.info.main, mb: 1 }} />
                                        <Typography variant="h4" sx={{ 
                                            fontWeight: 'bold',
                                            fontSize: { xs: '1.5rem', sm: '2rem' }
                                        }}>
                                            {stats.completed || 0}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, color: 'text.secondary' }}>
                                            Completed
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Card sx={{ 
                                    textAlign: 'center', 
                                    bgcolor: theme.palette.warning.light + '20',
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'translateY(-4px)' },
                                    cursor: 'pointer'
                                }}>
                                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                        <Pending sx={{ fontSize: { xs: 24, sm: 32 }, color: theme.palette.warning.main, mb: 1 }} />
                                        <Typography variant="h4" sx={{ 
                                            fontWeight: 'bold',
                                            fontSize: { xs: '1.5rem', sm: '2rem' }
                                        }}>
                                            {stats.pending || 0}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, color: 'text.secondary' }}>
                                            Pending
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}

                    {/* Search Bar */}
                    <Paper sx={{ 
                        mb: 3, 
                        p: { xs: 1.5, sm: 2 }, 
                        display: 'flex', 
                        alignItems: 'center',
                        backgroundColor: theme.palette.grey[50],
                        borderRadius: 2
                    }}>
                        <Search sx={{ mx: 1, color: 'text.secondary' }} />
                        <TextField 
                            fullWidth 
                            placeholder="Search by student name, supervisor name, email, or assignment ID..." 
                            variant="standard" 
                            value={searchTerm} 
                            onChange={(e) => setSearchTerm(e.target.value)} 
                            InputProps={{ disableUnderline: true }}
                            size={isMobile ? "small" : "medium"}
                        />
                    </Paper>

                    {/* Active Filters */}
                    {filters.status && (
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 1, 
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            mt: 2,
                            mb: 2,
                            pt: 2,
                            borderTop: '1px solid',
                            borderColor: 'divider'
                        }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mr: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                Active filters:
                            </Typography>
                            {filters.status && (
                                <Chip
                                    label={`Status: ${statusOptions.find(s => s.value === filters.status)?.label || filters.status}`}
                                    onDelete={() => setFilters(prev => ({ ...prev, status: '' }))}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                />
                            )}
                            <Button
                                size="small"
                                onClick={clearFilters}
                                startIcon={<CloseIcon />}
                                sx={{ ml: 1 }}
                            >
                                Clear All
                            </Button>
                        </Box>
                    )}
                </Box>
                
                {/* Assignments Table */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Paper elevation={2} sx={{ 
                        borderRadius: 3, 
                        overflow: 'auto',
                        backgroundColor: 'white',
                    }}>
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table sx={{ minWidth: { xs: 900, sm: 1000, md: 1100 } }}>
                                <TableHead>
                                    <TableRow sx={{
                                        backgroundColor: theme.palette.primary.main,
                                        '& th': {
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' }
                                        }
                                    }}>
                                        <TableCell>#</TableCell>
                                        <TableCell>Assignment ID</TableCell>
                                        <TableCell>Student</TableCell>
                                        <TableCell>Supervisor</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Assigned Date</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((assignment, index) => {
                                        const studentAvatarColor = stringToColor(assignment.student?.name);
                                        const studentInitials = getInitials(assignment.student?.name);
                                        const studentProfileImage = getStudentProfileImage(assignment.student);
                                        
                                        const supervisorAvatarColor = stringToColor(assignment.supervisor?.name);
                                        const supervisorInitials = getInitials(assignment.supervisor?.name);
                                        const supervisorProfileImage = getSupervisorProfileImage(assignment.supervisor);
                                        
                                        return (
                                            <TableRow
                                                key={assignment.id}
                                                hover
                                                sx={{
                                                    '&:nth-of-type(odd)': {
                                                        backgroundColor: theme.palette.action.hover
                                                    }
                                                }}
                                            >
                                                <TableCell>
                                                    <Chip
                                                        label={page * rowsPerPage + index + 1}
                                                        color="primary"
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={assignment.assignment_id}
                                                        size="small"
                                                        color="info"
                                                        variant="outlined"
                                                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                        <Avatar
                                                            src={studentProfileImage}
                                                            sx={{
                                                                width: 40,
                                                                height: 40,
                                                                bgcolor: !studentProfileImage ? studentAvatarColor : 'transparent',
                                                                fontSize: '1rem',
                                                                fontWeight: 'bold'
                                                            }}
                                                        >
                                                            {!studentProfileImage && studentInitials}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                                {assignment.student?.name}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <Email sx={{ fontSize: 12 }} />
                                                                {assignment.student?.email}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                        <Avatar
                                                            src={supervisorProfileImage}
                                                            sx={{
                                                                width: 40,
                                                                height: 40,
                                                                bgcolor: !supervisorProfileImage ? supervisorAvatarColor : 'transparent',
                                                                fontSize: '1rem',
                                                                fontWeight: 'bold'
                                                            }}
                                                        >
                                                            {!supervisorProfileImage && supervisorInitials}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                                {assignment.supervisor?.name}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {assignment.supervisor?.academic_title}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        icon={getStatusIcon(assignment.status)}
                                                        label={assignment.formatted_status}
                                                        size="small"
                                                        color={getStatusColor(assignment.status)}
                                                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                    {formatCreatedAtDate(assignment.assigned_date)}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title="View Details">
                                                        <IconButton onClick={() => handleOpenView(assignment)} color="primary" size="small">
                                                            <ViewIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50, 100]}
                            component="div"
                            count={filteredData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{ mt: 2 }}
                        />

                        {filteredData.length === 0 && (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                py: { xs: 6, sm: 8 },
                                color: 'text.secondary'
                            }}>
                                <Assignment sx={{ fontSize: { xs: 48, sm: 60 }, mb: 2 }} />
                                <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    No assigned students found
                                </Typography>
                                <Typography variant="body2">Try adjusting your search query or filters</Typography>
                            </Box>
                        )}
                    </Paper>
                </motion.div>

                {/* Filter Dialog */}
                <Dialog
                    open={filterOptions}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="sm"
                    disableEscapeKeyDown
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            handleCloseOptions();
                        }
                    }}
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ textAlign: 'center', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Filter Options
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: { xs: 1, sm: 2 } }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<RefreshIcon />}
                                onClick={clearFilters}
                                sx={{ textTransform: 'none' }}
                                size={isMobile ? "small" : "medium"}
                            >
                                Show All Records
                            </Button>

                            <Divider>OR</Divider>

                            <Typography variant="subtitle1" sx={{ textAlign: 'center', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                Filter by Status
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                                        <InputLabel>Assignment Status</InputLabel>
                                        <Select
                                            name="status"
                                            value={filters.status}
                                            onChange={handleFilterChange}
                                            label="Assignment Status"
                                        >
                                            <MenuItem value="">All Status</MenuItem>
                                            {statusOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {getStatusIcon(option.value)}
                                                        {option.label}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button
                            color="error"
                            onClick={handleCloseOptions}
                            variant="contained"
                            size={isMobile ? "small" : "medium"}
                        >
                            Close
                        </Button>
                        <Button
                            onClick={handleCloseOptions}
                            color="primary"
                            variant="contained"
                            size={isMobile ? "small" : "medium"}
                        >
                            Apply Filters
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* View Details Dialog */}
                <Dialog
                    open={openViewDialog}
                    onClose={handleCloseView}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="md"
                    disableEscapeKeyDown
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' },
                            maxHeight: '90vh',
                            borderRadius: 3
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, borderBottom: `1px solid ${theme.palette.divider}` }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Assignment color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    Assignment Details
                                </Typography>
                            </Box>
                            {selectedAssignment && (
                                <Chip 
                                    icon={getStatusIcon(selectedAssignment.status)} 
                                    label={selectedAssignment.formatted_status} 
                                    color={getStatusColor(selectedAssignment.status)}
                                    size="small"
                                />
                            )}
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        {selectedAssignment && (
                            <Box sx={{ pt: 2 }}>
                                {/* Assignment ID and Info */}
                                <Paper sx={{ p: 2, mb: 3, bgcolor: theme.palette.grey[50], borderRadius: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: theme.palette.primary.main }}>
                                        Assignment Information
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Badge fontSize="small" color="action" />
                                                <strong>Assignment ID:</strong> {selectedAssignment.assignment_id}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CalendarToday fontSize="small" color="action" />
                                                <strong>Assigned Date:</strong> {formatCreatedAtDate(selectedAssignment.assigned_date)}
                                            </Typography>
                                        </Grid>
                                        {selectedAssignment.completion_date && (
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <CheckCircle fontSize="small" color="action" />
                                                    <strong>Completion Date:</strong> {formatCreatedAtDate(selectedAssignment.completion_date)}
                                                </Typography>
                                            </Grid>
                                        )}
                                        {selectedAssignment.assigned_by && (
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Person fontSize="small" color="action" />
                                                    <strong>Assigned By:</strong> {selectedAssignment.assigned_by}
                                                </Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Paper>

                                {/* Student Details */}
                                <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: theme.palette.primary.main }}>
                                        <School color="primary" /> Student Information
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Person fontSize="small" color="action" />
                                                <strong>Name:</strong> {selectedAssignment.student?.name}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Email fontSize="small" color="action" />
                                                <strong>Email:</strong> {selectedAssignment.student?.email}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Phone fontSize="small" color="action" />
                                                <strong>Phone:</strong> {selectedAssignment.student?.contact_phone || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Flag fontSize="small" color="action" />
                                                <strong>Nationality:</strong> {selectedAssignment.student?.nationality || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Badge fontSize="small" color="action" />
                                                <strong>Registration ID:</strong> {selectedAssignment.student?.registration_id}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <School fontSize="small" color="action" />
                                                <strong>University:</strong> {selectedAssignment.student?.university || 'N/A'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>

                                {/* Supervisor Details */}
                                <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: theme.palette.secondary.main }}>
                                        <SupervisorAccount color="secondary" /> Supervisor Information
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Person fontSize="small" color="action" />
                                                <strong>Name:</strong> {selectedAssignment.supervisor?.name}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Email fontSize="small" color="action" />
                                                <strong>Email:</strong> {selectedAssignment.supervisor?.email}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Work fontSize="small" color="action" />
                                                <strong>Academic Title:</strong> {selectedAssignment.supervisor?.academic_title}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>

                                {/* Assignment Notes */}
                                {selectedAssignment.assignment_notes && (
                                    <Paper sx={{ p: 2, borderRadius: 2, bgcolor: theme.palette.grey[50] }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1, color: theme.palette.info.main }}>
                                            <Description color="info" /> Assignment Notes
                                        </Typography>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                                            {selectedAssignment.assignment_notes}
                                        </Typography>
                                    </Paper>
                                )}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button onClick={handleCloseView} variant="contained" size={isMobile ? "small" : "medium"}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default AssignedStudent;